import type { UseInfiniteQueryResult } from "@tanstack/react-query";
import type { UserType } from "../entities/users";
import type { RoleType } from "../entities/roles";
import type { TagType } from "../entities/tags";

export type SortOptionsTypes = {
  label: string;
  value: string;
};

export const sortDirectionEnum = {
  asc: "asc",
  desc: "desc",
} as const;

export type SortDirectionType =
  (typeof sortDirectionEnum)[keyof typeof sortDirectionEnum];

export type FilterStatus = {
  label: string;
  value: boolean;
  field: string;
  icon?: {
    name: string;
    type: "far" | "fas";
    color: "info" | "action" | "success" | "warning" | "danger" | "neutral";
  };
};

export const filterTypesEnum = {
  dates: "dates",
  textsearch: "textsearch",
  status: "status",
  hierarchyStatus: "hierarchyStatus",
  basicsearch: "basicsearch",
} as const;

export type FilterType = (typeof filterTypesEnum)[keyof typeof filterTypesEnum];

export type CommonInfiniteQueryType<
  TData = unknown,
  TFilters = Record<string, string>,
> = (filters: TFilters) => UseInfiniteQueryResult<TData, Error>;

type FilterTypeCommon = {
  label: string;
  field: string;
};

type FilterTypeStatus = {
  type: typeof filterTypesEnum.status;
  options: { field: string; label: string }[];
} & FilterTypeCommon;

type FilterTypeSearchtext = {
  type: typeof filterTypesEnum.textsearch;
  field: string;
  searchField: string;
  searchType: SearchType;
  options?: never;
  query?: CommonInfiniteQueryType;
} & FilterTypeCommon;

export type FilterTypeList =
  | FilterTypeStatus
  | FilterTypeSearchtext
  | ({
      type: Exclude<
        FilterType,
        typeof filterTypesEnum.status | typeof filterTypesEnum.textsearch
      >;
      options?: never;
    } & FilterTypeCommon);

type FilterCommonFields = {
  label: string;
  result: string;
  field: string;
  id: number;
  isUpdated: boolean;
};

export type CurrentFiltersType = { [key: string]: string };

export type FilterBasicSearchType = FilterCommonFields & {
  type: "basicsearch";
};

export type FilterDateType = FilterCommonFields & {
  type: "dates";
  shouldDisabledFutureDates?: boolean;
};

export const searchTypesEnum = {
  user: "user",
  roles: "roles",
  tags: "tag",
  positions: "positions",
  institutions: "institutions",
} as const;

export type SearchType = (typeof searchTypesEnum)[keyof typeof searchTypesEnum];

type SelectedMap = {
  [searchTypesEnum.user]: UserType[];
  [searchTypesEnum.roles]: RoleType[];
  [searchTypesEnum.tags]: TagType[];
  [searchTypesEnum.positions]: CatalogSearchItem[];
  [searchTypesEnum.institutions]: CatalogSearchItem[];
};

export type SelectionOptionType =
  | UserType[]
  | RoleType[]
  | TagType[]
  | CatalogSearchItem[];
export type MultiEntitySelections = {
  [searchTypesEnum.user]?: UserType[];
  [searchTypesEnum.roles]?: RoleType[];
  [searchTypesEnum.tags]?: TagType[];
  [searchTypesEnum.positions]?: CatalogSearchItem[];
  [searchTypesEnum.institutions]?: CatalogSearchItem[];
};

export type FilterSearchTypeBase = FilterCommonFields & {
  type: "textsearch";
  field: string;
  searchField: string;
  query?: CommonInfiniteQueryType;
  searchType: SearchType;
};

export type FilterSearchType = FilterSearchTypeBase & {
  selected?: SelectedMap[FilterSearchTypeBase["searchType"]];
};

export type FilterStatusType = FilterCommonFields & {
  type: "status";
  status: FilterStatus[];
};

export type FilterHierarchyStatusType = FilterCommonFields & {
  type: "hierarchyStatus";
  groups: {
    label: string;
    status: {
      label: string;
      value: boolean;
      field: string;
      children: FilterStatus[];
    }[];
  }[];
};

export type FilterOption =
  | FilterDateType
  | FilterSearchType
  | FilterStatusType
  | FilterBasicSearchType
  | FilterHierarchyStatusType;

export type Generalized<T> = {
  [K in keyof T]: string;
};

type UsersSearchData = {
  type: "user";
  name: string;
  mail: string;
};

export type SearchDataTypes = UsersSearchData;

export type CatalogSearchItem = {
  id: string;
  name?: string;
  nombre?: string;
  description?: string;
  activo?: boolean;
};
